import React, { useCallback, useRef } from 'react';
import * as _ from 'lodash'
import componentServices from '../componentServices';
import * as actions from '../actions';
import valueFilters from '../valueFilters';
import util from '../util';
import { AsyncButton, CurrencyInput, Header, JobCostLockable, PercentInput, PinnableStats } from './sharedComponents';
import { IJobCostChildProps } from './jobCostEdit';
import InstallSheetEdit, { IInstallSheetRef } from './installSheetEdit';
import { getInstallSheets } from 'Scripts/schemaHelper';

export default function JobCostEditInstallationService({ fetchReport, installSheetKey, jobCost, updateModel }: IJobCostChildProps & { installSheetKey?: string}) {
	const installSheetRef = useRef<IInstallSheetRef>(null);
	const handleUpdate = useCallback((path, autoSave, e) => {
		const newVal = (autoSave) ? valueFilters.clean(e.target.value) : e.target.value;

		// this is currently only allowed on the original estimate. once it is a contract these values cannot be changed
		updateModel((jobCost, lineUpdateCallback) => {
			const installSheet = getInstallSheets(jobCost)[installSheetKey];

			jobCost.withStrictFilters(strictJobCost => {
				if (path === 'unitCost') {
					installSheet.unitCost = newVal;

					if (autoSave) {
						_.forEach(installSheetRef.current.configItems, item => {
							// only alter items that use the costFactor - autoCalculated items are on there own
							if (item.costFactor && !item.isAutoCalculated) {
								item.cost = installSheet.unitCost * item.costFactor;
								const lineItem = _.find(strictJobCost.lineItems, { type: item.type, group: item.group, description: item.description, installSheetKey });
								if (lineItem) {
									lineItem.cost = item.cost;
									lineUpdateCallback(lineItem, 'cost');
								}
							}
						});
					}
				}
				else if (path === 'margin') {
					installSheet.margin = newVal;

					if (autoSave) {
						_.forEach(_.filter(strictJobCost.getLineItemsByType(['installation', 'installation-custom', 'service']), { installSheetKey }), lineItem => {
							lineItem.margin = installSheet.margin;
							lineUpdateCallback(lineItem, 'margin');
						});
					}
				}
			});
		}, { autoSave: autoSave });
	}, [installSheetKey, jobCost, updateModel]);

	const printReport = useCallback(() => {
		return fetchReport(`internal/installation/${installSheetKey}`);
	}, [installSheetKey, fetchReport])

	const pinStats = useCallback(() => {
		actions.setPinnedStatState({ key: 'jcInstallationService', isPinned: true });
	}, []);

	const collapseAll = useCallback(() => {
		installSheetRef.current.collapseAll();
	}, []);

	const installSheet = jobCost && getInstallSheets(jobCost)[installSheetKey];
	const hasPinnedStats = !!componentServices.store.getIn(['viewState', 'pinnedStats', 'jcInstallationService']);
	const installationUnitPrice = installSheet.unitCost || jobCost.installationUnitPrice || 0;
	const installationMargin = installSheet.margin || jobCost.margin;
	const titlebarActions = [<button onClick={ collapseAll }><span>Collapse All</span></button>, <AsyncButton className="pdf" asyncAction={printReport} label="Print" />];

	let serviceSell = 0;
	let serviceCost = 0;
	let installationSell = 0;
	let installationCost = 0;
	let travelSell = 0;
	let travelCost = 0;

	jobCost.withStrictFilters(strictJobCost => {
		_.forEach(_.filter(strictJobCost.getLineItemsByType(['installation', 'installation-custom', 'service', 'travel']), { installSheetKey }), item => {
			if (['installation', 'installation-custom'].indexOf(item.type) !== -1) {
				installationSell += item.sellPrice || 0;
				installationCost += item.totalCost || 0;
			}
			else if (item.type === 'service') {
				serviceSell += item.sellPrice || 0;
				serviceCost += item.totalCost || 0;
			}
			else if (item.type === 'travel') {
				travelSell += item.sellPrice || 0;
				travelCost += item.totalCost || 0;
			}
		});
	});

	return (
		<section>
			<Header title={ getInstallSheets(jobCost).length() > 1 ? `Installation & Service ${installSheet.sortIndex + 1} • ${installSheet.name}` : 'Installation & Service' } actions={ titlebarActions } />
			<div>
				<section className="expander expanded jobCostInstallationService">
					<div className="inner">
						<table className="compact" cellPadding="0" cellSpacing="0">
							<tbody>
								<JobCostLockable jobCost={ jobCost }>{ ({ getClassName }) =>
									(<tr className={ getClassName() }>
										<td />
										<td>Install Unit Cost</td>
										<td className="cost"><CurrencyInput value={util.reactVal(installationUnitPrice)} onChange={ e => handleUpdate('unitCost', false, e) } onBlur={ e => handleUpdate('unitCost', true, e) } /></td>
										<td />
									</tr>)
								}
								</JobCostLockable>
								<JobCostLockable jobCost={ jobCost }>{ ({ getClassName }) =>
									(<tr className={ getClassName() }>
										<td />
										<td>Margin</td>
										<td className="cost"><PercentInput value={ util.reactVal(installationMargin) } onChange={ e => handleUpdate('margin', false, e) } onBlur={ e => handleUpdate('margin', true, e) } /></td>
										<td />
									</tr>)
								}
								</JobCostLockable>
							</tbody>
						</table>
					</div>
					{ jobCost && installSheet? <InstallSheetEdit jobCost={jobCost} updateModel={updateModel} installSheet={ installSheet } ref={installSheetRef} /> : null }
					{ hasPinnedStats ? <section className="stats total pinnable out" /> : null }
					<PinnableStats isPinned={ hasPinnedStats }>
						<section className={ hasPinnedStats ? 'stats total compact' : 'stats total pinnable'}>
							{ hasPinnedStats ? null : <button className="icon iconOnly pinItem" onClick={ pinStats }><span /></button> }
							<div className="cost column">
								{util.formatAsCurrency(installationCost)} <span>INSTALLATION COST</span>
							</div>
							<div className="cost column">
								{util.formatAsCurrency(installationSell)} <span>INSTALLATION SELL</span>
							</div>
							<div className="cost column">
								{util.formatAsCurrency(serviceCost)} <span>SERVICE COST</span>
							</div>
							<div className="cost column">
								{util.formatAsCurrency(serviceSell)} <span>SERVICE SELL</span>
							</div>
							<div className="cost column">
								{util.formatAsCurrency(travelCost)} <span>TRAVEL COST</span>
							</div>
							<div className="cost column">
								{util.formatAsCurrency(travelSell)} <span>TRAVEL SELL</span>
							</div>
						</section>
					</PinnableStats>
				</section>
			</div>
		</section>

	);
}